import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavBar from '../components/common/TopNavBar';
import useFetch from '../hooks/useFetch';
import './LibraryPage.css';

const SKILL_CONFIG = {
  listening: {
    label: 'Listening',
    icon: 'headset',
    color: '#6BB5E3',
  },
  reading_writing: {
    label: 'Reading & Writing',
    icon: 'edit_note',
    color: '#1E5C2E',
  },
};

const STATUS_CONFIG = {
  mastered:          { label: 'Mastered',    emoji: '🏆', bg: '#f0fdf4', color: '#16a34a', border: '#16a34a' },
  in_progress:       { label: 'Đang học',    emoji: '📈', bg: '#fffbeb', color: '#d97706', border: '#d97706' },
  needs_improvement: { label: 'Cần ôn',      emoji: '⚠️', bg: '#fef2f2', color: '#dc2626', border: '#dc2626' },
  unexplored:        { label: 'Chưa làm',    emoji: '🔘', bg: '#f8fafc', color: '#94a3b8', border: '#94a3b8' },
};

/* ── Helpers ─────────────────────────────────────────────────── */

function useAuthFetch(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) { setLoading(false); return; }
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error('fetch failed'); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

function getStatusForScore(score, attempts) {
  if (attempts === 0) return 'unexplored';
  if (score >= 80 && attempts >= 3) return 'mastered';
  if (score < 50) return 'needs_improvement';
  return 'in_progress';
}

/* ── Skeleton ────────────────────────────────────────────────── */
const SkeletonCards = ({ count = 4 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="lib-shimmer" style={{ height: 130, borderRadius: 16 }} />
    ))}
  </div>
);

/* ── Overview Stats Bar ──────────────────────────────────────── */
const OverviewStatsBar = ({ performanceData }) => {
  if (!performanceData?.skills) return null;

  const skills = performanceData.skills;
  const allParts = skills.flatMap(s => s.parts);

  const overallScore = skills.length > 0
    ? Math.round(skills.reduce((sum, s) => sum + s.average_score, 0) / skills.length)
    : 0;

  const masteredCount = allParts.filter(p => p.score >= 80 && p.total_attempts >= 3).length;
  const inProgressCount = allParts.filter(p => p.total_attempts > 0 && !(p.score >= 80 && p.total_attempts >= 3) && p.score >= 50).length;
  const needsWorkCount = allParts.filter(p => p.total_attempts > 0 && p.score < 50).length;

  const stats = [
    { label: 'Tổng thể', value: `${overallScore}%`, emoji: '📊', bg: '#eef2ff', color: '#6366f1' },
    { label: 'Mastered', value: masteredCount, emoji: '🏆', bg: '#f0fdf4', color: '#16a34a' },
    { label: 'Đang học', value: inProgressCount, emoji: '📈', bg: '#fffbeb', color: '#d97706' },
    { label: 'Cần ôn', value: needsWorkCount, emoji: '⚠️', bg: '#fef2f2', color: '#dc2626' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {stats.map(s => (
        <div key={s.label} className="bg-white rounded-xl shadow-[0px_4px_16px_rgba(94,46,26,0.05)] p-4 flex items-center gap-3">
          <div style={{ width: 40, height: 40, borderRadius: 12, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
            {s.emoji}
          </div>
          <div>
            <div className="font-headline" style={{ fontWeight: 900, fontSize: 20, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Part Card (enriched with mastery) ───────────────────────── */
const PartCard = ({ part, skillColor, perfData, onClick }) => {
  const perf = perfData || {};
  const score = Math.round(perf.score || 0);
  const attempts = perf.total_attempts || 0;
  const status = getStatusForScore(score, attempts);
  const statusCfg = STATUS_CONFIG[status];

  return (
    <div className="lib-part-card" onClick={onClick} style={{
      background: '#fff',
      padding: 20,
      borderRadius: 16,
      boxShadow: '0 8px 24px rgba(94,46,26,0.06)',
      cursor: 'pointer',
      borderLeft: `4px solid ${statusCfg.border}20`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <h3 className="font-headline" style={{ fontWeight: 700, fontSize: 16, color: '#3E2B1E', marginBottom: 4, lineHeight: 1.3 }}>
            {part.name}
          </h3>
          <p style={{ fontSize: 12, fontWeight: 500, color: '#C4A060' }}>
            {part.total_question} Quiz{attempts > 0 ? ` · ${attempts} lần làm` : ''}
          </p>
        </div>
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: statusCfg.bg,
          color: statusCfg.color,
          padding: '4px 10px',
          borderRadius: 8,
          whiteSpace: 'nowrap',
        }}>
          {statusCfg.emoji} {attempts > 0 ? `${score}%` : statusCfg.label}
        </span>
      </div>

      {/* Progress bar */}
      {attempts > 0 && (
        <div style={{ width: '100%', height: 6, background: '#F0EAD6', borderRadius: 999, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ height: '100%', width: `${score}%`, background: statusCfg.color, borderRadius: 999, transition: 'width 0.6s ease' }} />
        </div>
      )}

      <p style={{ fontSize: 12, color: '#6B5D4F', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {part.description}
      </p>
    </div>
  );
};

/* ── Skill Column (enriched) ─────────────────────────────────── */
const SkillColumn = ({ skill, parts, loading, error, perfParts }) => {
  const navigate = useNavigate();
  const config = SKILL_CONFIG[skill];

  const perfMap = {};
  (perfParts || []).forEach(p => { perfMap[p.code] = p; });

  return (
    <div className="lib-slide-in" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className="material-symbols-outlined" style={{ color: config.color, fontSize: 28, fontVariationSettings: "'FILL' 0, 'wght' 400" }}>
          {config.icon}
        </span>
        <h2 className="font-headline" style={{ fontSize: 20, fontWeight: 800, color: config.color }}>
          {config.label}
        </h2>
      </div>

      {loading && <SkeletonCards count={skill === 'listening' ? 5 : 7} />}
      {error && (
        <div style={{ textAlign: 'center', padding: 32, color: '#ef4444' }}>
          <span style={{ fontSize: 32 }}>😕</span>
          <p style={{ fontWeight: 600, marginTop: 8 }}>Không tải được dữ liệu</p>
        </div>
      )}
      {!loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {(parts || []).map((part) => (
            <PartCard
              key={part.id}
              part={part}
              skillColor={config.color}
              perfData={perfMap[part.id]}
              onClick={() => navigate(`/library/part/${part.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Speaking Locked Column ──────────────────────────────────── */
const SpeakingLockedColumn = () => (
  <div className="lib-slide-in" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span className="material-symbols-outlined" style={{ color: '#E8A020', fontSize: 28, fontVariationSettings: "'FILL' 0, 'wght' 400" }}>record_voice_over</span>
      <h2 className="font-headline" style={{ fontSize: 20, fontWeight: 800, color: '#E8A020' }}>Speaking</h2>
    </div>
    <div style={{
      position: 'relative', background: 'rgba(255,255,255,0.4)', border: '2px dashed rgba(196,160,96,0.3)',
      borderRadius: 16, minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 32, textAlign: 'center', overflow: 'hidden',
    }}>
      <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ width: 64, height: 64, background: 'rgba(232,160,32,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          <span className="material-symbols-outlined" style={{ color: '#E8A020', fontSize: 32, fontVariationSettings: "'FILL' 1" }}>lock</span>
        </div>
        <h3 className="font-headline" style={{ fontSize: 18, fontWeight: 700, color: '#3E2B1E', marginBottom: 8 }}>Đang phát triển</h3>
        <p style={{ color: '#6B5D4F', fontWeight: 500, fontSize: 13, maxWidth: 200 }}>Tính năng luyện nói AI sẽ sớm xuất hiện</p>
      </div>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(232,160,32,0.05)', backdropFilter: 'blur(2px)' }} />
    </div>
  </div>
);

/* ── AI Suggestion Banner (fixed bottom) ─────────────────────── */
const AISuggestionBanner = () => {
  const navigate = useNavigate();
  return (
    <section style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
      background: 'rgba(240,234,214,0.95)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      padding: '16px 24px', borderTop: '1px solid rgba(196,160,96,0.2)', boxShadow: '0 -4px 24px rgba(94,46,26,0.1)',
    }}>
      <div style={{ maxWidth: 1152, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ fontSize: 32, flexShrink: 0 }}>🦉</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#3E2B1E', fontWeight: 500, fontSize: 14, lineHeight: 1.5 }}>
            <strong className="font-headline" style={{ color: '#2A6B30' }}>HuPath gợi ý: </strong>
            Ôn lại <strong style={{ color: '#2A6B30' }}>Past Simple</strong> và{' '}
            <strong style={{ color: '#2A6B30' }}>Animals</strong> — 2 chủ đề cần cải thiện nhất!
          </p>
        </div>
        <button onClick={() => navigate('/skill-practice')} className="font-headline" style={{
          background: '#F99D27', color: '#fff', fontWeight: 800, padding: '10px 24px', borderRadius: 12,
          border: 'none', cursor: 'pointer', boxShadow: '0 4px 14px rgba(249,157,39,0.3)',
          display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 14, transition: 'all 0.2s ease', whiteSpace: 'nowrap',
        }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Làm ngay
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_forward</span>
        </button>
      </div>
    </section>
  );
};

/* ══════════════════════════════════════════════════════════════ */
/* ══  MAIN PAGE COMPONENT  ════════════════════════════════════ */
/* ══════════════════════════════════════════════════════════════ */

const LibraryPage = () => {
  const apiBase = process.env.REACT_APP_API_URL;

  // Exercise catalog (public, no auth)
  const { data: listeningParts, loading: lLoad, error: lErr } = useFetch(
    `${apiBase}/exercises/exercise-types?skill=listening`
  );
  const { data: rwParts, loading: rwLoad, error: rwErr } = useFetch(
    `${apiBase}/exercises/exercise-types?skill=reading_writing`
  );

  // Performance data (requires auth)
  const { data: perfData } = useAuthFetch(`${apiBase}/users/me/performance-map`);

  // Build perf lookup per skill
  const perfBySkill = {};
  if (perfData?.skills) {
    perfData.skills.forEach(s => { perfBySkill[s.code] = s.parts; });
  }

  return (
    <div className="min-h-screen ob-sky-voyager font-body text-hp-on-surface relative overflow-x-hidden hp-trail-pattern">
      <TopNavBar />

      <main className="max-w-6xl mx-auto px-6 py-8 relative z-10">

        {/* ── Header ─────────────────────────────────── */}
        <section className="bg-white rounded-xl shadow-[0px_8px_24px_rgba(94,46,26,0.06)] p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-hp-on-surface font-headline mb-2">
                Thư Viện Bài Tập
              </h1>
              <p className="text-hp-on-surface/60 font-medium">
                Khám phá kho bài tập theo kỹ năng
              </p>
            </div>
            <div className="relative w-full md:w-80">
              <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-hupath-khaki text-xl">search</span>
              <input
                type="text"
                placeholder="Tìm kiếm bài tập, chủ đề..."
                className="w-full pl-12 pr-4 py-3 rounded-full border-none shadow-sm focus:ring-4 focus:ring-hupath-scout/20 text-hp-on-surface bg-gray-50 outline-none text-sm"
              />
            </div>
          </div>
        </section>

        {/* ── Overview Stats ─────────────────────────── */}
        <OverviewStatsBar performanceData={perfData} />

        {/* ── Tab Content ────────────────────────────── */}
        <div style={{ paddingBottom: 80 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 32,
          }}>
            <SkillColumn skill="listening" parts={listeningParts} loading={lLoad} error={lErr} perfParts={perfBySkill['listening']} />
            <SkillColumn skill="reading_writing" parts={rwParts} loading={rwLoad} error={rwErr} perfParts={perfBySkill['reading_writing']} />
            <SpeakingLockedColumn />
          </div>
        </div>

      </main>

      <AISuggestionBanner />

      {/* Background decorations */}
      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[150px] bg-white/60 blur-[60px] rounded-[100%] pointer-events-none -z-10"></div>
      <div className="fixed top-20 right-[-100px] w-64 h-64 bg-amber-200/20 blur-[80px] rounded-full pointer-events-none -z-10"></div>
      <div className="fixed bottom-20 left-[-100px] w-80 h-80 bg-hupath-scout/10 blur-[100px] rounded-full pointer-events-none -z-10"></div>
    </div>
  );
};

export default LibraryPage;
